type LogLevel = 'debug' | 'info' | 'warn' | 'error'
type LogEmitter = (level: LogLevel, message: string, data?: Record<string, unknown>) => void

interface DebouncerOptions {
  /** Debounce window in ms (default: 10000) */
  windowMs?: number
}

interface PendingEntry {
  count: number
  timer: ReturnType<typeof setTimeout>
  data?: Record<string, unknown>
}

/**
 * Suppresses repeated log messages within a time window.
 * First occurrence is emitted immediately. Subsequent identical messages
 * are counted and a summary is emitted when the window expires.
 */
export class LogDebouncer {
  private pending = new Map<string, PendingEntry>()
  private windowMs: number

  constructor(
    private emit: LogEmitter,
    options: DebouncerOptions = {}
  ) {
    this.windowMs = options.windowMs ?? 10_000
  }

  log(level: LogLevel, message: string, data?: Record<string, unknown>): void {
    const key = `${level}:${message}`
    const existing = this.pending.get(key)

    if (existing) {
      existing.count++
      return
    }

    // Emit immediately
    this.emit(level, message, data)

    // Start counting window
    const entry: PendingEntry = {
      count: 0,
      data,
      timer: setTimeout(() => {
        this.pending.delete(key)
        if (entry.count > 0) {
          this.emit(level, `${message} (repeated ${entry.count} more times)`, data)
        }
      }, this.windowMs)
    }
    this.pending.set(key, entry)
  }

  dispose(): void {
    for (const entry of this.pending.values()) {
      clearTimeout(entry.timer)
    }
    this.pending.clear()
  }
}
